import { useMemo } from "react";
import { t } from "i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import { PageBody } from "@app/PageReady/PageBody";
import { IconLineArrowLeft } from "@kalodata/kds-design-icons";
import { classifyReferenceVideoIdentity } from "@apis/videoRemakeRequest";
import { VideoRemakeStudio } from "./studio/VideoRemakeStudio";
import { VideoRemakeStudioProvider } from "./studio/VideoRemakeStudioContext";
import type { ReferenceVideoState } from "./studio/types";

/**
 * 解析 Kalodata 跨域跳转传入的 URL query：
 *   /video-remake/studio?referenceVideoUrl=...&referenceVideoId=...&videoSource=Kalodata
 */
const readQueryReference = (search: URLSearchParams): ReferenceVideoState | null => {
  const duration = Number(search.get("durationSeconds") ?? search.get("duration") ?? "");
  const videoAssetId = Number(search.get("videoAssetId") ?? search.get("assetId") ?? "");
  const identity = classifyReferenceVideoIdentity({
    videoUrl: search.get("referenceVideoUrl") ?? search.get("videoUrl"),
    videoAssetId: Number.isFinite(videoAssetId) && videoAssetId > 0 ? videoAssetId : undefined,
    videoId: search.get("referenceVideoId") ?? search.get("videoId"),
    videoSource: search.get("videoSource"),
  });
  if (!identity) return null;
  return {
    ...identity,
    coverUrl: search.get("coverImageUrl") ?? undefined,
    durationSeconds: Number.isFinite(duration) && duration > 0 ? duration : undefined,
  };
};

export function VideoRemakeStudioPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const initialReferenceVideo = useMemo<ReferenceVideoState | null>(() => {
    const stateRef = (location.state as { referenceVideo?: ReferenceVideoState } | null)?.referenceVideo;
    if (stateRef?.videoUrl) return stateRef;
    return readQueryReference(searchParams);
  }, [location.state, searchParams]);

  const state = location.state as { language?: string; region?: string } | null;
  const initialLanguage = state?.language ?? searchParams.get("language") ?? undefined;
  const initialRegion = state?.region ?? searchParams.get("region") ?? undefined;

  return (
    <PageBody icon={<IconLineArrowLeft />} title={t("Video Remake")} contentClassName="flex min-h-0 justify-center">
      <VideoRemakeStudioProvider
        initialReferenceVideo={initialReferenceVideo}
        initialLanguage={initialLanguage}
        initialRegion={initialRegion}
      >
        <VideoRemakeStudio />
      </VideoRemakeStudioProvider>
    </PageBody>
  );
}
